import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useThemeMode } from '../../hooks/v0/useThemeMode';
import { useAtom, useAtomValue } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { tasksAtom, fetchTasksAtom } from '../../atoms/v0/taskAtoms';
import { fetchCreditsUsageAtom } from '../../atoms/v3/creditsUsageAtoms';
import { ProfileHeader, SettingsGroup, SettingsItem, CreditsUsageItem, MembershipStatusItem } from './components/me';
import { supabase } from '../../lib/supabase';
import { useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

import i18n from '../../lib/i18n';
import IOSLanguageSelector from './components/ios/IOSLanguageSelector';
import IOSThemeSelector from './components/ios/IOSThemeSelector';
import { useAuth } from '../../lib/auth/AuthProvider';
import { isPremiumAtom } from '../../atoms/v0/subscriptionAtoms';
import { DocumentViewer } from './components/common/DocumentViewer';
import { useUpdateChecker, useUpdateModal } from '../../hooks/v3/useUpdateChecker';
import { UpdateModal } from '../../components/v3/UpdateModal';
import { HabitCard } from './components/HabitCard';
import { getUserDisplayName, getUserDisplayEmail, isLocalUser, shouldShowAccountUpgrade, canManageMultipleAccounts } from '../../utils/userUtils';
import { AnonymousLogoutModal } from './components/AnonymousLogoutModal';
import { useAppOnboarding } from '../../hooks/useAppOnboarding';
import { ScheduledConversationsSection } from './components/me/ScheduledConversationsSection';
import { NotificationPreferencesSection } from './components/me/NotificationPreferencesSection';
import { fetchUserPreferencesAtom } from '../../atoms/v3/userPreferencesAtoms';
import { trackSettingsEvents } from '../../lib/analyticsEnhanced';
import { getUserLanguagePreference, getLanguageDisplayName } from '../../utils/languageUtils';
import { KeymapModal } from './components/tasks/KeymapModal';
import { isTouchDevice } from '../../utils/platformUtils';


// 持久化存储 showHiddenHabits 状态
const showHiddenHabitsAtom = atomWithStorage('showHiddenHabits', false);

// 持久化存储 showHabits 状态
const showHabitsAtom = atomWithStorage('showHabits', true);

export const Me: React.FC = () => {
  const navigate = useNavigate();
  const { themeMode } = useThemeMode();
  const toast = useToast();
  const { t } = useTranslation(['v3/common', 'v3/me']);

  // States
  const { user } = useAuth();
  const isPremium = useAtomValue(isPremiumAtom);
  const [tasks] = useAtom(tasksAtom);
  const [, fetchTasks] = useAtom(fetchTasksAtom);
  // const [currentLanguage] = useAtom(currentLanguageAtom); // 不再需要，使用 getUserLanguagePreference
  // const [, changeLanguage] = useAtom(changeLanguageAtom); // 暂时不需要

  // 选择器和文档查看器状态
  const [isLanguageSelectorOpen, setIsLanguageSelectorOpen] = useState(false);
  const [isThemeSelectorOpen, setIsThemeSelectorOpen] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showTermsOfService, setShowTermsOfService] = useState(false);
  const [showAnonymousLogoutModal, setShowAnonymousLogoutModal] = useState(false);
  const [isKeymapModalOpen, setIsKeymapModalOpen] = useState(false);

  // 引导相关
  const { resetOnboarding } = useAppOnboarding();

  // 更新检查相关
  const { currentVersion, checkState, manualCheckForUpdates } = useUpdateChecker();
  useUpdateModal(); // 初始化更新模态框

  // 获取用户偏好
  const [, fetchUserPreferences] = useAtom(fetchUserPreferencesAtom);

  // 获取 Credits 用量
  const [, fetchCreditsUsage] = useAtom(fetchCreditsUsageAtom);

  // 加载任务数据和用户偏好
  useEffect(() => {
    fetchTasks();
    fetchUserPreferences().catch(console.error);
    fetchCreditsUsage().catch(console.error);
  }, [fetchTasks, fetchUserPreferences, fetchCreditsUsage]);

  // 登出
  const handleSignOut = async () => {
    // 检查是否为匿名用户
    if (user && isLocalUser(user)) {
      // 匿名用户显示确认对话框
      setShowAnonymousLogoutModal(true);
      return;
    }

    // 正常用户直接登出
    // 使用强制登出工具函数，确保即使在离线状态下也能登出
    import('../../utils/authUtils').then(({ forceSignOut }) => {
      forceSignOut(
        navigate,
        toast,
        t('v3/me:toast.signOutSuccess'),
        t('v3/me:toast.signOutFailed')
      );
    }).catch(error => {
      console.error(t('v3/common:errors.general'), error);
      // 回退到原始登出逻辑
      try {
        supabase.auth.signOut().catch(console.error);
        toast({
          title: t('v3/me:toast.signOutSuccess'),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      } catch (error) {
        console.error(t('v3/me:toast.signOutFailed'), error);
        toast({
          title: t('v3/me:toast.signOutFailed'),
          description: error instanceof Error ? error.message : t('v3/me:toast.unknownError'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      } finally {
        // 无论如何都清理本地 session/token
        try {
          localStorage.clear();
          sessionStorage.clear();
        } catch (e) {
          // 忽略本地清理异常
        }
        navigate('/');
      }
    });
  };

  // 计算任务统计
  const completedTasks = tasks.filter(task => task.done).length;
  const pendingTasks = tasks.filter(task => !task.done).length;

  // 使用 useMemo 优化习惯任务过滤逻辑，避免不必要的重新计算
  const habitTasks = useMemo(() =>
    tasks.filter(task => task.isHabit === true && !task.done),
    [tasks]
  );

  // 隐藏的习惯：有 recurrenceGroupId 但 isHabit 为 false 的任务（表示曾经是习惯但被隐藏了）
  const hiddenHabitTasks = useMemo(() =>
    tasks.filter(task =>
      task.isHabit === false &&
      task.recurrenceGroupId &&
      !task.done
    ),
    [tasks]
  );

  // 隐藏习惯展开状态 - 使用持久化存储
  const [showHiddenHabits, setShowHiddenHabits] = useAtom(showHiddenHabitsAtom);

  // 习惯区域展开状态 - 使用持久化存储
  const [showHabits, setShowHabits] = useAtom(showHabitsAtom);



  // 注意：习惯统计计算已在 HabitCard 组件中实现
  // HabitCard 组件使用更精确的算法，基于 doneAt 时间戳和真实的任务完成历史

  // 处理个人资料点击
  const handleProfileClick = () => {
    // 记录个人资料访问事件
    trackSettingsEvents.settingsPageAccessed('profile', {
      user_type: shouldShowAccountUpgrade(user) ? 'local' : 'registered',
      can_manage_accounts: canManageMultipleAccounts(user)
    });

    if (shouldShowAccountUpgrade(user)) {
      // 本地用户点击跳转到账号升级页面
      navigate('/v3/account-management');
    } else if (canManageMultipleAccounts(user)) {
      // 正式用户点击跳转到账号管理页面
      navigate('/v3/account-management');
    } else {
      handleFeatureNotImplemented(t('v3/me:profile.edit'));
    }
  };

  // 处理语言选择器打开
  const handleOpenLanguageSelector = () => {
    trackSettingsEvents.settingsPageAccessed('language', {
      current_language: i18n.language
    });
    setIsLanguageSelectorOpen(true);
  };

  // 处理主题选择器打开
  const handleOpenThemeSelector = () => {
    trackSettingsEvents.settingsPageAccessed('theme', {
      current_theme: themeMode === 'dark' ? 'dark' : themeMode === 'light' ? 'light' : 'system'
    });
    setIsThemeSelectorOpen(true);
  };

  // 处理未实现的功能
  const handleFeatureNotImplemented = (featureName: string) => {
    toast({
      title: t('v3/me:toast.comingSoon'),
      description: t('v3/me:toast.featureInDevelopment', { feature: featureName }),
      status: 'info',
      duration: 3000,
      isClosable: true,
      position: 'top'
    });
  };

  // 处理检查更新
  const handleCheckForUpdates = async () => {
    try {
      await manualCheckForUpdates();

      // 根据检查状态显示不同的提示
      if (checkState === 'no-update') {
        toast({
          title: t('v3/me:update.alreadyLatest', '已是最新版本'),
          description: t('v3/me:update.currentVersion', '当前版本: {{version}}', { version: currentVersion }),
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      } else if (checkState === 'error') {
        toast({
          title: t('v3/me:update.checkFailed', '检查更新失败'),
          description: t('v3/me:update.tryAgainLater', '请稍后重试'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
      // 如果有更新，会自动显示更新模态框，不需要额外提示
    } catch (error) {
      console.error('检查更新失败:', error);
      toast({
        title: t('v3/me:update.checkFailed', '检查更新失败'),
        description: error instanceof Error ? error.message : t('v3/me:update.unknownError', '未知错误'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
    }
  };

  // 处理重新体验引导
  const handleRestartOnboarding = () => {
    resetOnboarding();
    navigate('/');
    toast({
      title: t('v3/me:onboarding.restarted', '引导已重置'),
      description: t('v3/me:onboarding.restartedDescription', '返回任务页面开始体验引导'),
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  return (
    <div className="p-4 pt-6 pb-16 font-ios mt-[env(safe-area-inset-top)]">
      {/* 个人资料 */}
      <SettingsGroup>
        <ProfileHeader
          name={getUserDisplayName(user)}
          email={getUserDisplayEmail(user)}
          avatar={user?.user_metadata?.avatar_url}
          isPremium={isPremium}
          onClick={handleProfileClick}
        />
        {/* 账号状态提示 */}
        {shouldShowAccountUpgrade(user) && (
          <div className="px-4 pb-4">
            <div className="p-3 bg-ios-blue/10 dark:bg-ios-dark-blue/20 rounded-ios-lg">
              <p className="text-ios-footnote text-ios-blue dark:text-ios-dark-blue">
                {t('v3/me:account.upgradePrompt', '☁️ 点击上方头像绑定账号以联网云同步')}
              </p>
            </div>
          </div>
        )}

        {/* 匿名用户红色警告 */}
        {isLocalUser(user) && (
          <div className="px-4 pb-4">
            <div
              className="p-3 bg-ios-red/10 dark:bg-ios-red/20 rounded-ios-lg cursor-pointer active:bg-ios-red/20 dark:active:bg-ios-red/30 transition-colors"
              onClick={handleProfileClick}
            >
              <p className="text-ios-footnote text-ios-red dark:text-ios-red">
                ⚠️ {t('v3/me:account.anonymousWarning', '您目前为未绑定账号的匿名用户，退出或切换设备后将无法重新登陆，请尽快点击上方头像区域绑定账号。')}
              </p>
            </div>
          </div>
        )}
      </SettingsGroup>

      {/* 会员与用量 */}
      <SettingsGroup title={t('v3/me:membershipAndUsage.title', '会员与用量')}>
        <MembershipStatusItem />
        <div className="border-t border-ios-gray5 dark:border-ios-dark-gray5 mx-4" />
        <CreditsUsageItem />
      </SettingsGroup>

      {/* 习惯监控 - 仅在有习惯任务时显示 */}
      {habitTasks.length > 0 && (
        <div className="mb-6">
          {/* 可点击的标题 */}
          <button
            onClick={() => setShowHabits(!showHabits)}
            className="w-full text-ios-title3 font-semibold text-ios-black dark:text-white mb-4 px-1 flex items-center justify-between hover:opacity-70 transition-opacity duration-200"
            title={showHabits ? t('v3/me:habits.collapse', '收起习惯监控') : t('v3/me:habits.expand', '展开习惯监控')}
          >
            <div className="flex items-center">
              <span className="text-ios-purple dark:text-ios-dark-purple mr-2">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L13.09 8.26L20 9L13.09 9.74L12 16L10.91 9.74L4 9L10.91 8.26L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </span>
              {t('v3/me:habits.title', '习惯监控')}
            </div>
            <svg
              className={`w-4 h-4 transition-transform duration-200 ${showHabits ? 'rotate-180' : ''}`}
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* 习惯列表 - 条件渲染 */}
          {showHabits && (
            <div className="space-y-4">
              {habitTasks.map(habit => (
                <HabitCard key={habit.id} task={habit} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* 隐藏的习惯 */}
      {hiddenHabitTasks.length > 0 && (
        <div className="mb-6">
          {/* 展开/折叠按钮 */}
          <button
            onClick={() => setShowHiddenHabits(!showHiddenHabits)}
            className="flex items-center space-x-1 text-ios-caption text-ios-gray dark:text-ios-dark-gray hover:text-ios-black dark:hover:text-white transition-colors duration-200 px-1"
          >
            <span>
              {showHiddenHabits ? t('v3/me:habits.hideHidden', '收起已隐藏习惯') : t('v3/me:habits.showHidden', '展开已隐藏习惯')} ({hiddenHabitTasks.length})
            </span>
            <svg
              className={`w-3 h-3 transition-transform duration-200 ${showHiddenHabits ? 'rotate-180' : ''}`}
              viewBox="0 0 12 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>

          {/* 隐藏的习惯列表 */}
          {showHiddenHabits && (
            <div className="space-y-4 mt-4">
              {hiddenHabitTasks.map(habit => (
                <HabitCard key={habit.id} task={habit} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* 旧版 */}
      <SettingsGroup title={t('v3/me:legacy.title', '旧版')}>
        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-orange dark:text-ios-dark-orange" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:legacy.focus', '专注')}
          onClick={() => navigate('/v3/focus')}
          iconBgColor="bg-ios-orange/10 dark:bg-ios-dark-orange/20"
        />

        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-purple dark:text-ios-dark-purple" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="2" y="3" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="2"/>
              <path d="M8 21h8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M12 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 7v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              <path d="M9 10h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          }
          title={t('v3/me:legacy.clockMode', '时钟模式')}
          onClick={() => navigate('/v3/clock-mode')}
          iconBgColor="bg-ios-purple/10 dark:bg-ios-dark-purple/20"
        />

        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11L12 14L20 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20 12V18C20 19.1046 19.1046 20 18 20H6C4.89543 20 4 19.1046 4 18V6C4 4.89543 4.89543 4 6 4H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:legacy.tasks', '提醒事项')}
          onClick={() => navigate('/v3/tasks')}
          iconBgColor="bg-ios-blue/10 dark:bg-ios-dark-blue/20"
        />

        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-red dark:text-ios-dark-red" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M8 2V6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 10H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:legacy.calendar', '日历')}
          onClick={() => navigate('/v3/calendar')}
          iconBgColor="bg-ios-red/10 dark:bg-ios-dark-red/20"
        />

        {/* 重新体验引导按钮 - 只在启用应用引导时显示 */}
        {import.meta.env.VITE_APP_ONBOARDING_ENABLED === 'true' && (
          <SettingsItem
            icon={
              <svg className="w-5 h-5 text-ios-teal dark:text-ios-dark-teal" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            }
            title={t('v3/me:onboarding.restart', '重新体验引导')}
            onClick={handleRestartOnboarding}
            iconBgColor="bg-ios-teal/10 dark:bg-ios-dark-teal/20"
          />
        )}
      </SettingsGroup>


      {/* 外观 */}
      <SettingsGroup title={t('v3/me:appearance.title')}>
        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-orange dark:text-ios-dark-orange" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 17C14.7614 17 17 14.7614 17 12C17 9.23858 14.7614 7 12 7C9.23858 7 7 9.23858 7 12C7 14.7614 9.23858 17 12 17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 1V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 21V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4.22 4.22L5.64 5.64" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18.36 18.36L19.78 19.78" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M1 12H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M21 12H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M4.22 19.78L5.64 18.36" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M18.36 5.64L19.78 4.22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
          title={t('v3/me:appearance.themeMode', '主题模式')}
          iconBgColor="bg-ios-orange/10 dark:bg-ios-dark-orange/20"
          rightElement={
            <span className="text-ios-gray dark:text-ios-dark-gray text-ios-subhead">
              {themeMode === 'light'
                ? t('v3/me:appearance.lightMode')
                : themeMode === 'dark'
                  ? t('v3/me:appearance.darkMode')
                  : t('v3/me:appearance.systemMode')}
            </span>
          }
          onClick={handleOpenThemeSelector}
        />

        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-green dark:text-ios-dark-green" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M2 12H22" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M12 2C14.5013 4.73835 15.9228 8.29203 16 12C15.9228 15.708 14.5013 19.2616 12 22C9.49872 19.2616 8.07725 15.708 8 12C8.07725 8.29203 9.49872 4.73835 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          }
          title={t('v3/me:appearance.language')}
          iconBgColor="bg-ios-green/10 dark:bg-ios-dark-green/20"
          rightElement={
            <span className="text-ios-gray dark:text-ios-dark-gray text-ios-subhead">
              {getLanguageDisplayName(getUserLanguagePreference() || 'system')}
            </span>
          }
          onClick={handleOpenLanguageSelector}
        />

        {/* 域名切换功能已移除 - 现在由 app-startup-initializer.js 自动处理 */}
      </SettingsGroup>

      {/* 设置 */}

      {/* <SettingsGroup title={t('v3/me:settings.title')}> */}
        {/* 通用设置 - 暂时注释，功能未实现 */}
        {/* <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:settings.general')}
          onClick={() => handleFeatureNotImplemented(t('v3/me:settings.general'))}
          iconBgColor="bg-ios-blue/10 dark:bg-ios-dark-blue/20"
        /> */}

        {/* 时间和日期设置 - 暂时注释，功能未实现 */}
        {/* <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-red dark:text-ios-dark-red" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:settings.timeAndDate')}
          onClick={() => handleFeatureNotImplemented(t('v3/me:settings.timeAndDate'))}
          iconBgColor="bg-ios-red/10 dark:bg-ios-dark-red/20"
        /> */}

        {/* 任务设置 - 暂时注释，功能未实现 */}
        {/* <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-purple dark:text-ios-dark-purple" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11L12 14L22 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:settings.taskSettings')}
          onClick={() => handleFeatureNotImplemented(t('v3/me:settings.taskSettings'))}
          iconBgColor="bg-ios-purple/10 dark:bg-ios-dark-purple/20"
        /> */}

        {/* 个性化设置 - 暂时注释，业务没有闭环, 且有bug, 暂时下掉 */}
        {/* <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-pink dark:text-ios-dark-pink" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:personalization')}
          onClick={() => navigate('/v3/persona-onboarding')}
          iconBgColor="bg-ios-pink/10 dark:bg-ios-dark-pink/20"
        /> */}
      {/* </SettingsGroup> */}

      {/* 通知偏好 */}
      <SettingsGroup title={t('v3/me:notifications.title', '通知偏好')}>
        <NotificationPreferencesSection />
      </SettingsGroup>

      {/* 定时对话 */}
      <SettingsGroup title={t('v3/me:scheduledConversations.title', '定时对话')}>
        <ScheduledConversationsSection />
      </SettingsGroup>


      {/* 语言选择器 */}
      <IOSLanguageSelector
        isOpen={isLanguageSelectorOpen}
        onClose={() => setIsLanguageSelectorOpen(false)}
      />

      {/* 主题选择器 */}
      <IOSThemeSelector
        isOpen={isThemeSelectorOpen}
        onClose={() => setIsThemeSelectorOpen(false)}
      />

      {/* 支持 */}
      <SettingsGroup title={t('v3/me:support.title')}>
        {/* 版本信息和检查更新 */}
        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M21 16V8C20.9996 7.64928 20.9071 7.30481 20.7315 7.00116C20.556 6.69751 20.3037 6.44536 20 6.27L13 2.27C12.696 2.09446 12.3511 2.00205 12 2.00205C11.6489 2.00205 11.304 2.09446 11 2.27L4 6.27C3.69626 6.44536 3.44398 6.69751 3.26846 7.00116C3.09294 7.30481 3.00036 7.64928 3 8V16C3.00036 16.3507 3.09294 16.6952 3.26846 16.9988C3.44398 17.3025 3.69626 17.5546 4 17.73L11 21.73C11.304 21.9055 11.6489 21.9979 12 21.9979C12.3511 21.9979 12.696 21.9055 13 21.73L20 17.73C20.3037 17.5546 20.556 17.3025 20.7315 16.9988C20.9071 16.6952 20.9996 16.3507 21 16Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.5 4.21L12 6.81L16.5 4.21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7.5 19.79V14.6L3 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12L16.5 14.6V19.79" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 22.08V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:support.version', '版本信息')}
          iconBgColor="bg-ios-blue/10 dark:bg-ios-dark-blue/20"
          rightElement={
            <div className="flex items-center space-x-2">
              <span className="text-ios-gray dark:text-ios-dark-gray text-ios-subhead">
                {t('v3/me:support.versionNumber', 'v{{version}}', { version: currentVersion })}
              </span>
              {checkState === 'checking' && (
                <div className="w-4 h-4 border-2 border-ios-blue border-t-transparent rounded-full animate-spin"></div>
              )}
            </div>
          }
          onClick={handleCheckForUpdates}
        />

        {/* 帮助与反馈 - 暂时注释，功能未实现 */}
        {/* <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-teal dark:text-ios-dark-teal" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M9.09 9C9.3251 8.33167 9.78915 7.76811 10.4 7.40913C11.0108 7.05016 11.7289 6.91894 12.4272 7.03871C13.1255 7.15849 13.7588 7.52152 14.2151 8.06353C14.6713 8.60553 14.9211 9.29152 14.92 10C14.92 12 11.92 13 11.92 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 17H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:support.helpAndFeedback')}
          onClick={() => handleFeatureNotImplemented(t('v3/me:support.helpAndFeedback'))}
          iconBgColor="bg-ios-teal/10 dark:bg-ios-dark-teal/20"
        /> */}

        {/* 关于 - 暂时注释，功能未实现 */}
        {/* <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-indigo dark:text-ios-dark-indigo" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 16V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M12 8H12.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:support.about')}
          onClick={() => handleFeatureNotImplemented(t('v3/me:support.about'))}
          iconBgColor="bg-ios-indigo/10 dark:bg-ios-dark-indigo/20"
        /> */}

        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H19C19.5304 3 20.0391 3.21071 20.4142 3.58579C20.7893 3.96086 21 4.46957 21 5V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 7H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 11H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M7 15H11" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:support.privacyPolicy')}
          onClick={() => setShowPrivacyPolicy(true)}
          iconBgColor="bg-ios-blue/10 dark:bg-ios-dark-blue/20"
        />

        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-orange dark:text-ios-dark-orange" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 14H4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20 6H12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20 10H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M20 18H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:support.termsOfService')}
          onClick={() => setShowTermsOfService(true)}
          iconBgColor="bg-ios-orange/10 dark:bg-ios-dark-orange/20"
        />

        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-purple dark:text-ios-dark-purple" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 11.08V8L14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 16L22 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19 13L22 16L19 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:developer.title')}
          onClick={() => navigate('/v3/developer-settings')}
          iconBgColor="bg-ios-purple/10 dark:bg-ios-dark-purple/20"
        />

        {/* 键盘快捷键 - 仅限非触控设备 */}
        {!isTouchDevice() && (
          <SettingsItem
            icon={
              <svg className="w-5 h-5 text-ios-gray dark:text-ios-dark-gray" viewBox="0 0 24 24" fill="none" stroke="currentColor" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="6" width="18" height="12" rx="2" ry="2" strokeWidth="2"/>
                <path d="M7 10h.01" strokeWidth="2" strokeLinecap="round"/>
                <path d="M11 10h.01" strokeWidth="2" strokeLinecap="round"/>
                <path d="M15 10h.01" strokeWidth="2" strokeLinecap="round"/>
                <path d="M7 14h.01" strokeWidth="2" strokeLinecap="round"/>
                <path d="M11 14h4" strokeWidth="2" strokeLinecap="round"/>
                <path d="M17 14h.01" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            }
            title={t('common:keymap.title')}
            onClick={() => setIsKeymapModalOpen(true)}
            iconBgColor="bg-ios-gray/10 dark:bg-ios-dark-gray/20"
          />
        )}
      </SettingsGroup>

      {/* 账户 */}
      <SettingsGroup title={t('v3/me:account.title')}>
        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-blue dark:text-ios-dark-blue" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="12" cy="7" r="4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 21V19C20.9949 18.1172 20.6979 17.2608 20.1553 16.5644C19.6126 15.868 18.8548 15.3707 18 15.15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:account.management', '账号管理')}
          onClick={() => navigate('/v3/account-management')}
          iconBgColor="bg-ios-blue/10 dark:bg-ios-dark-blue/20"
        />

        <SettingsItem
          icon={
            <svg className="w-5 h-5 text-ios-red dark:text-ios-dark-red" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 17L21 12L16 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          }
          title={t('v3/me:account.signOut')}
          onClick={handleSignOut}
          iconBgColor="bg-ios-red/10 dark:bg-ios-dark-red/20"
        />
      </SettingsGroup>

      {/* 文档查看器模态框 */}
      <DocumentViewer
        isOpen={showPrivacyPolicy}
        onClose={() => setShowPrivacyPolicy(false)}
        documentUrl="/privacy-policy.html"
        title={t('v3/me:support.privacyPolicy')}
      />

      <DocumentViewer
        isOpen={showTermsOfService}
        onClose={() => setShowTermsOfService(false)}
        documentUrl="/terms-of-service.html"
        title={t('v3/me:support.termsOfService')}
      />

      {/* 更新模态框 */}
      <UpdateModal />

      {/* 匿名用户退出确认模态框 */}
      <AnonymousLogoutModal
        isOpen={showAnonymousLogoutModal}
        onClose={() => setShowAnonymousLogoutModal(false)}
      />

      {/* 键盘快捷键说明弹窗 */}
      <KeymapModal
        isOpen={isKeymapModalOpen}
        onClose={() => setIsKeymapModalOpen(false)}
        currentPageType="me"
      />
    </div>
  );
};
